import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';

/**
 * HTTP error interceptor for handling common HTTP errors globally.
 * 
 * Intercepts HTTP error responses and provides consistent error handling:
 * - 401 Unauthorized: Redirects to login page
 * - 403 Forbidden: Displays "Access Denied" message
 * - 404 Not Found: Displays "Resource Not Found" message
 * - 400 Bad Request: Displays validation errors from response
 * - 500 Server Error: Displays generic error message
 * - Network errors: Displays "Connection Error" message
 * 
 * This ensures consistent error handling across the application without
 * requiring each component to handle these errors individually.
 * 
 * @example
 * ```typescript
 * // Register in app.config.ts
 * provideHttpClient(
 *   withInterceptors([errorInterceptor])
 * )
 * ```
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unexpected error occurred';

      if (error.error instanceof ErrorEvent) {
        errorMessage = `Connection Error: ${error.error.message}`;
        console.error('Network error:', error.error.message);
      } else {
        switch (error.status) {
          case 401: {
            // Only redirect to login if the user was previously authenticated
            // (genuine session expiry). Skip for auth/profile — that's the
            // startup check and a 401 there just means "not logged in yet".
            const isAuthCheck = req.url.includes('/api/auth/profile');
            const wasAuthenticated = authService.isAuthenticated();

            if (!isAuthCheck && wasAuthenticated) {
              console.warn('401 Unauthorized: Session expired, redirecting to login');
              authService.logout().catch(() => undefined);
              router.navigate(['/login'], { replaceUrl: true });
            }
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          }

          case 403:
            console.warn('403 Forbidden: Access denied');
            errorMessage = 'Access Denied: You do not have permission to access this resource.';
            break;

          case 404:
            console.warn('404 Not Found:', req.url);
            errorMessage = 'Resource Not Found: The requested resource could not be found.';
            break;

          case 400:
            console.warn('400 Bad Request:', error.error);
            if (error.error?.message) {
              errorMessage = `Validation Error: ${error.error.message}`;
            } else if (error.error?.errors) {
              const errors = Array.isArray(error.error.errors)
                ? error.error.errors.join(', ')
                : JSON.stringify(error.error.errors);
              errorMessage = `Validation Error: ${errors}`;
            } else {
              errorMessage = 'Bad Request: Please check your input and try again.';
            }
            break;

          case 500:
            console.error('500 Server Error:', error.error);
            errorMessage = 'Server Error: An error occurred on the server. Please try again later.';
            break;

          case 0:
            console.error('Network error: No response from server');
            errorMessage = 'Connection Error: Unable to connect to the server. Please check your internet connection.';
            break;

          default:
            console.error(`HTTP Error ${error.status}:`, error.error);
            errorMessage = `Error ${error.status}: ${error.message || 'An unexpected error occurred'}`;
        }
      }

      const enhancedError = new HttpErrorResponse({
        error: { ...error.error, userMessage: errorMessage },
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url || undefined
      });

      return throwError(() => enhancedError);
    })
  );
};
